/**
 * Das gemeinsame Modell einer Event-Card — alles, was Web und App aus einem Event *ableiten*,
 * bevor irgendetwas gezeichnet wird.
 *
 * Grund: bis hierher stand jede dieser zehn Ableitungen **zweimal** — Monatsnamen, Datumsformat,
 * Wochentag-Range, Countdown, Status, Disziplin-Beschriftung, Region, Entfernung, Anmeldestand,
 * Streckenchips. Sie sind auseinandergelaufen, ohne dass es jemandem auffiel: die App kürzte den
 * Titel nach zwei Zeilen, schrieb den Wochentag in eine zweite Zeile und stellte Disziplin und
 * Countdown in den Bildkopf, während das Web beides längst im weißen Body zeigte. Für den Nutzer
 * waren das zwei Produkte.
 *
 * Die Regel ist dieselbe wie „gerechnet wird im Backend, das Frontend formatiert", nur eine Ebene
 * tiefer: **hier wird abgeleitet, die Plattform zeichnet nur noch.** Wer etwas an der Card ändert,
 * das kein Pixel ist, ändert es hier — und damit an beiden Stellen.
 */
import { isPopularEvent } from './popular';
import { isOffSeason, offSeasonLabel, permanentBadge } from './permanentSeason';
import {
  CyclingEvent, EventCategory, EventPromotion, PromotionTier, activePromotion, eventCategories,
  promotionRenderTier,
} from './types';
import {
  CATEGORY_COLORS, CATEGORY_COLORS_DARK, CATEGORY_LABELS, displayBundesland, eventTypesLabel,
} from './brand';
import { daysUntilEvent, haversineDistance, isTimeVariant, orderedVariantLabels, parseLocalDay, registrationBadge } from './utils';

const MON = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const WD = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WD_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** Der Sonderzustand, der den Countdown ersetzt. */
export type EventCardStatus = 'ABGESAGT' | 'AUSGEBUCHT' | 'VORLÄUFIG';

/**
 * Beschriftung und Farben der Status-Pille. Sie standen getrennt in beiden Projekten und waren
 * verschieden: das Web zeigte Abgesagt auf `#C0392B`, die App auf `#B23A48`. Dieselbe Aussage
 * darf nicht zwei Rottöne haben.
 */
export const EVENT_STATUS_LABEL: Record<EventCardStatus, string> = {
  'ABGESAGT': 'Abgesagt',
  'AUSGEBUCHT': 'Ausgebucht',
  'VORLÄUFIG': 'Vorläufig',
};

export const EVENT_STATUS_PILL: Record<EventCardStatus, { bg: string; fg: string }> = {
  'ABGESAGT':   { bg: '#C0392B', fg: '#FFFFFF' },
  'AUSGEBUCHT': { bg: '#C0392B', fg: '#FFFFFF' },
  'VORLÄUFIG':  { bg: '#EFEBEB', fg: '#7A756F' },
};

/**
 * Farbe des Zustands als Wort in der Kickerzeile (Event-Zeile der Startseite). Keine Pille:
 * Nur die Absage ist eine Warnung und bekommt Rot. „Ausgebucht" heißt nur, dass man nicht mehr
 * reinkommt, und steht deshalb in Rost. „Vorläufig" ist eine Auskunft und bleibt grau.
 * Alle drei Farben erreichen auf Weiß mindestens 4,5:1 (6,6 / 8,3 / 5,4).
 */
export const EVENT_STATUS_TEXT: Record<EventCardStatus, string> = {
  'ABGESAGT':   '#B42318',
  'AUSGEBUCHT': '#8C3B0B',
  'VORLÄUFIG':  '#6A6A6A',
};

/** Dasselbe auf der dunklen Card (`renderTier === 'INVERTED'`, Grund `#241A1F`): 6,9 / 8,6 / 7,4 zu 1. */
export const EVENT_STATUS_TEXT_DARK: Record<EventCardStatus, string> = {
  'ABGESAGT':   '#FF8A80',
  'AUSGEBUCHT': '#F0A35E',
  'VORLÄUFIG':  '#B6ABA5',
};

export interface EventCardDate {
  /** Große Tageszahl; bei mehrtägig im selben Monat die Range („25–26"). */
  day: string;
  /** „NOV 26" — bei einer Range über den Monatswechsel nur der Monatsname des Beginns. */
  mon: string;
  /** Nur bei einer Range über den Monatswechsel: das zweite Tag+Monat-Paar. */
  second: { day: string; mon: string } | null;
  /** „Montag" bzw. „Sa–So". `null` ab einer Woche Länge, weil die Range dann nichts mehr sagt. */
  weekday: string | null;
}


export interface EventCardModel {
  /** `null` bei einem permanenten Event: die Card zeigt dort `permanentLabel` statt eines Datums. */
  date: EventCardDate | null;
  /** „Permanent · Apr–Okt" an der Stelle des Datums (`permanentBadge`), sonst `null`. */
  permanentLabel: string | null;
  /**
   * „heute" / „morgen" / „in 75 Tagen"; `null`, sobald das Event vorbei ist. Bei einem
   * permanenten Event außerhalb der Saison „wieder ab April", in Saison und ganzjährig `null`.
   */
  countdown: string | null;
  /**
   * Wo die Card den Countdown zeigt. `head`: als stille Pille neben dem Herz im Bildkopf (seit
   * 09/2026, vorher in Berry hinter der Ortszeile). `region`: bei einer laufenden Buchung, weil
   * dort die Pflicht-Kennzeichnung neben dem Herz steht und auf Phone-Breite Datum, Countdown,
   * Herz und Kennzeichnung nicht in eine Zeile passen.
   */
  countdownSlot: 'head' | 'region';
  status: EventCardStatus | null;
  /**
   * „Rennrad · Gravel · RTF" — alle Kategorien, dahinter die Eventart. Tragen einzelne Strecken
   * eine eigene Art, folgen sie („Gravel · Gravel-Tour · Gravel-Race").
   */
  categoryLabel: string;
  /** Farbe der Leitkategorie, auf der invertierten Card ihr aufgehellter Zwilling. */
  categoryColor: string;
  /** „Erbach · Hessen" */
  region: string;
  /** Auf 5 km gerundet, nur bei aktiver Standortsuche. */
  distanceKm: number | null;
  /** Leiser Anmeldestand-Chip; ersetzt den Countdown **nicht**. */
  registration: { label: string; long: string; tone: 'positive' | 'neutral' } | null;
  chips: string[];
  /** Strecken als Spanne für eine einzige Zeile („42–154 km"), die Liste steht in `chips`. */
  kmSpan: string | null;
  more: string | null;
  /** Laufende bezahlte Hervorhebung, sonst `null`. */
  promo: EventPromotion | null;
  /**
   * **Wie** gezeichnet wird — nicht, was gebucht ist. Eine gebuchte Bühne ohne Event-Foto fällt
   * auf `FRAME` zurück (`promotionRenderTier`). Wer stattdessen `promo.tier` abfragt, zeichnet
   * für genau diese Buchung gar keine Hervorhebung: die App stand so ohne Rahmen da, und die
   * Pflicht-Kennzeichnung war auf dem dunklen Kopf kaum zu lesen.
   */
  renderTier: PromotionTier | null;
  inverted: boolean;
  cancelled: boolean;
  /** Permanentes Event: kein Datum, statt des Countdowns der Saisonstand (siehe `CyclingEvent.permanent`). */
  permanent: boolean;
  /**
   * Permanentes Event außerhalb seiner Saison: die Card steht grau, damit sie sich von den
   * fahrbaren absetzt, wenn jemand „Events außerhalb der Saison anzeigen" gewählt hat.
   */
  offSeason: boolean;
  /**
   * Leises „Beliebt"-Badge (`isPopularEvent`). Auf einer gebuchten Card (Rahmen, dunkel,
   * Bühne) bleibt es weg: dort steht schon die Pflicht-Kennzeichnung, und neben „ANZEIGE"
   * läse sich „Beliebt" wie Teil der Werbung.
   */
  popular: boolean;
}

export interface EventCardOptions {
  /** Ursprung der Standortsuche — nur dann wird „ca. X km entfernt" gerechnet. */
  userLat?: number | null;
  userLng?: number | null;
}

const two = (d: Date): string => String(d.getDate()).padStart(2, '0');
const monYear = (d: Date): string => `${MON[d.getMonth()].toUpperCase()} ${String(d.getFullYear()).slice(2)}`;

/**
 * Zerlegt „AUG 26" in Monat und zweistellige Jahreszahl, damit Web und App beide verschieden
 * gewichten können: Monat fett, Jahr normal. Gleich fett las sich „AUG 26" als „26. August"
 * (Nutzer-Feedback 07/2026). „JUL" ohne Jahr (Range über den Monatswechsel) → `year: null`.
 */
export function splitMonYear(mon: string): { month: string; year: string | null } {
  const [month, year] = mon.split(' ');
  return { month, year: year ?? null };
}

/**
 * Mehrtägig im selben Monat → Tagesrange in der großen Zahl („25–26 JUL 26"); über den
 * Monatswechsel hinweg → zwei Tag+Monat-Paare („31 JUL – 02 AUG 26").
 */
export function eventCardDate(event: Pick<CyclingEvent, 'eventDate' | 'endDate'>): EventCardDate {
  const start = new Date(`${event.eventDate}T00:00:00`);
  const multiDay = !!event.endDate && event.endDate > event.eventDate;
  const end = multiDay ? new Date(`${event.endDate}T00:00:00`) : null;
  const sameMonth = end != null
    && end.getMonth() === start.getMonth()
    && end.getFullYear() === start.getFullYear();

  const weekday = !end
    ? WD[start.getDay()]
    : (end.getTime() - start.getTime()) / 86400000 < 7
      ? `${WD_SHORT[start.getDay()]}–${WD_SHORT[end.getDay()]}`
      : null;

  if (end && !sameMonth) {
    return {
      day: two(start),
      // Gleiches Jahr → die Jahreszahl steht nur hinten, sonst zweimal.
      mon: end.getFullYear() === start.getFullYear() ? MON[start.getMonth()].toUpperCase() : monYear(start),
      second: { day: two(end), mon: monYear(end) },
      weekday,
    };
  }
  return {
    day: end ? `${two(start)}–${two(end)}` : two(start),
    mon: monYear(start),
    second: null,
    weekday,
  };
}

/**
 * Ein mehrtägiges Event läuft gerade: Start <= heute <= Ende. Eintägige Events laufen nie in
 * diesem Sinn — für sie bleibt es bei „heute".
 */
export function isMultiDayEventRunning(
  event: Pick<CyclingEvent, 'eventDate' | 'endDate'>,
  now: Date = new Date(),
): boolean {
  if (!event.endDate || event.endDate.slice(0, 10) <= event.eventDate.slice(0, 10)) return false;
  const today = new Date(now.getTime());
  today.setHours(0, 0, 0, 0);
  const t = today.getTime();
  return parseLocalDay(event.eventDate).getTime() <= t && t <= parseLocalDay(event.endDate).getTime();
}

/**
 * Das Event ist vorbei: auch sein letzter Tag liegt vor heute. Ein permanentes Event hat keinen
 * Termin und ist nie vorbei. Dieselbe Grenze wie `eventCountdown` (dort `null`).
 */
export function isEventPast(
  event: Pick<CyclingEvent, 'eventDate' | 'endDate' | 'permanent'>,
  now: Date = new Date(),
): boolean {
  if (event.permanent) return false;
  return daysUntilEvent(event.eventDate, event.endDate, now) < 0;
}

/**
 * Die Merkliste teilt sich in Kommende (nach Datum) und Vergangene (die jüngste zuerst). Seit
 * dem 13.10.2026 zeigen Web und App die Vergangenen nur auf Wunsch („Vergangene zeigen"),
 * vorher standen sie oben vor allem, was noch ansteht.
 */
export function splitFavorites<T extends Pick<CyclingEvent, 'eventDate' | 'endDate' | 'permanent'>>(
  events: T[],
  now: Date = new Date(),
): { upcoming: T[]; past: T[] } {
  const upcoming = events.filter(e => !isEventPast(e, now))
    .sort((a, b) => a.eventDate.localeCompare(b.eventDate));
  const past = events.filter(e => isEventPast(e, now))
    .sort((a, b) => b.eventDate.localeCompare(a.eventDate));
  return { upcoming, past };
}

/**
 * „findet gerade statt" (laufendes Mehrtagesevent) / „heute" / „morgen" / „in N Tagen";
 * `null`, sobald das Event vorbei ist. Web-Card, App-Card und beide Detailseiten lesen hier.
 */
export function eventCountdown(
  event: Pick<CyclingEvent, 'eventDate' | 'endDate' | 'permanent' | 'seasonFrom' | 'seasonUntil'>,
  now: Date = new Date(),
): string | null {
  // Ein permanentes Event hat keinen Termin, also weder Countdown noch „vorbei" — an dieser
  // Stelle steht außerhalb der Saison, ab wann es wieder geht. „in Saison" stand bis zum
  // 13.10.2026 auch hier, sagte aber nichts: die Liste „Permanent" zeigt ohnehin nur, was gerade
  // fahrbar ist.
  if (event.permanent) return offSeasonLabel(event, now);
  const days = daysUntilEvent(event.eventDate, event.endDate, now);
  if (days < 0) return null;
  if (isMultiDayEventRunning(event, now)) return 'findet gerade statt';
  return days === 0 ? 'heute' : days === 1 ? 'morgen' : `in ${days} Tagen`;
}

/** Wie `eventCountdown`, für die Pille der Detailseiten großgeschrieben („Heute", „Findet gerade statt"). */
export function eventCountdownTitle(
  event: Pick<CyclingEvent, 'eventDate' | 'endDate' | 'permanent' | 'seasonFrom' | 'seasonUntil'>,
  now: Date = new Date(),
): string | null {
  const label = eventCountdown(event, now);
  if (!label || label.startsWith('in ')) return label;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function eventCardStatus(
  event: Pick<CyclingEvent, 'cancelled' | 'fullyBooked' | 'tentative'>,
): EventCardStatus | null {
  if (event.cancelled) return 'ABGESAGT';
  if (event.fullyBooked) return 'AUSGEBUCHT';
  if (event.tentative) return 'VORLÄUFIG';
  return null;
}

/**
 * Höchstens drei Streckenchips: die kürzeste, eine mittlere und die längste. Der Rest wird
 * gezählt („+2 weitere") statt weggelassen — sonst sieht eine Veranstaltung mit acht Strecken
 * aus wie eine mit dreien.
 */
export function eventCardChips(
  distances: CyclingEvent['distances'],
): { chips: string[]; more: string | null } {
  const labels = orderedVariantLabels(distances ?? []);
  if (labels.length === 0) return { chips: [], more: null };
  const chips = labels.length <= 3
    ? labels
    : [labels[0], labels[Math.floor((labels.length - 1) / 2)], labels[labels.length - 1]];
  const rest = labels.length - chips.length;
  return { chips, more: rest > 0 ? `+${rest} weitere` : null };
}

/**
 * Strecken als kurze Spanne: „42 km" bei einer, „42–154 km" bei mehreren. Ohne Kilometer die
 * erste Zeitvariante („24 h"). Für eine Zeile, in der die Liste aus `eventCardChips` keinen Platz hat.
 */
export function eventCardKmSpan(distances: CyclingEvent['distances']): string | null {
  const kms = (distances ?? []).filter(d => !isTimeVariant(d) && d.distanceKm > 0).map(d => d.distanceKm);
  if (kms.length > 0) {
    const min = Math.min(...kms), max = Math.max(...kms);
    return min === max ? `${min} km` : `${min}–${max} km`;
  }
  return orderedVariantLabels(distances ?? [])[0] ?? null;
}

export function eventCardModel(event: CyclingEvent, opts: EventCardOptions = {}): EventCardModel {
  const promo = activePromotion(event);
  const renderTier = promotionRenderTier(event);
  const inverted = renderTier === 'INVERTED';

  // Farbe folgt der Leitkategorie; genannt werden alle — ein Volksradfahren, das mit Rennrad,
  // Gravel und MTB gefahren werden kann, soll das auf der Card auch zeigen.
  const categories = eventCategories(event).map((c: EventCategory) => CATEGORY_LABELS[c] ?? c).join(' · ');
  const typeLabel = eventTypesLabel(event);

  const registration = registrationBadge(event);
  const chips = eventCardChips(event.distances);

  const { userLat, userLng } = opts;
  const distanceKm = userLat != null && userLng != null && event.latitude != null && event.longitude != null
    ? Math.round(haversineDistance(userLat, userLng, event.latitude, event.longitude) / 5) * 5
    : null;

  return {
    date: event.permanent ? null : eventCardDate(event),
    permanentLabel: permanentBadge(event),
    // Ein abgesagtes Event zählt nicht mehr herunter.
    countdown: event.cancelled ? null : eventCountdown(event),
    countdownSlot: promo ? 'region' : 'head',
    status: eventCardStatus(event),
    categoryLabel: typeLabel ? `${categories} · ${typeLabel}` : categories,
    categoryColor: (inverted ? CATEGORY_COLORS_DARK[event.category] : CATEGORY_COLORS[event.category]) ?? '#922C57',
    // Ohne Ort steht bei einem permanenten Event sein Gebiet („Erzgebirge · Sachsen").
    region: [event.city || event.areaName, displayBundesland(event.bundesland)].filter(Boolean).join(' · '),
    distanceKm,
    // Absage und „ausgebucht" stehen schon im Status — hier nur die Anmelde-Zustände. Ein
    // abgesagtes Event wirbt mit nichts mehr: weder Anmeldestand noch „Beliebt".
    registration: !event.cancelled && registration && (registration.tone === 'positive' || registration.tone === 'neutral')
      ? { label: registration.label, long: registration.long, tone: registration.tone }
      : null,
    chips: chips.chips,
    kmSpan: eventCardKmSpan(event.distances),
    more: chips.more,
    promo,
    renderTier,
    inverted,
    cancelled: !!event.cancelled,
    permanent: !!event.permanent,
    offSeason: isOffSeason(event),
    popular: !promo && !event.cancelled && isPopularEvent(event),
  };
}
